import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GripVertical } from 'lucide-react';

interface ComparisonSliderProps {
  beforeImage: string;
  afterImage: string;
  labelBefore?: string;
  labelAfter?: string;
}

export const ComparisonSlider: React.FC<ComparisonSliderProps> = ({
  beforeImage,
  afterImage,
  labelBefore = "Before",
  labelAfter = "After"
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback((clientX: number) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percentage = (x / rect.width) * 100;
      setSliderPosition(percentage);
    }
  }, []);

  const onMouseDown = (e: React.MouseEvent) => { e.stopPropagation(); setIsDragging(true); };
  const onTouchStart = (e: React.TouchEvent) => { e.stopPropagation(); setIsDragging(true); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const delta = e.key === 'ArrowLeft' ? -5 : e.key === 'ArrowRight' ? 5 : 0;
    if (!delta && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    e.stopPropagation();
    setSliderPosition(position => e.key === 'Home' ? 0 : e.key === 'End' ? 100 : Math.max(0, Math.min(100, position + delta)));
  };

  const onMouseUp = () => setIsDragging(false);
  const onTouchEnd = () => setIsDragging(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      handleMove(e.touches[0].clientX);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', onTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [isDragging, handleMove]);

  return (
    <div
      className="relative isolate w-full h-full select-none overflow-hidden bg-stone-100 group border border-stone-200"
      ref={containerRef}
    >
      {/* After Image (Background) */}
      <img
        src={afterImage}
        alt={labelAfter}
        className="absolute top-0 left-0 w-full h-full object-cover"
      />

      {/* Label After */}
      <div className="absolute top-6 right-6 z-10">
        <span className="bg-white/90 backdrop-blur-sm text-stone-900 px-4 py-1.5 text-xs font-bold tracking-wide uppercase border border-stone-900/10">
          {labelAfter}
        </span>
      </div>

      {/* Before Image (Foreground - Clipped) */}
      <div
        className="absolute top-0 left-0 w-full h-full overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={beforeImage}
          alt={labelBefore}
          className="absolute top-0 left-0 w-full h-full object-cover max-w-none"
          style={{ width: '100%', height: '100%' }} // Ensure it matches parent dimension exactly
        />
        {/* Label Before */}
        <div className="absolute top-6 left-6 z-10">
            <span className="bg-stone-900/90 backdrop-blur-sm text-white px-4 py-1.5 text-xs font-bold tracking-wide uppercase border border-white/10">
            {labelBefore}
            </span>
        </div>
      </div>

      {/* Slider Handle */}
      <div
        className="absolute top-0 bottom-0 w-[1px] bg-white z-20 cursor-ew-resize"
        role="slider"
        tabIndex={0}
        aria-label="Vergelijk voor en na"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={sliderPosition}
        aria-orientation="horizontal"
        style={{ left: `${sliderPosition}%`, touchAction: 'none' }}
        onKeyDown={onKeyDown}
        onClick={e => e.stopPropagation()}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white border border-stone-200 shadow-xl rounded-full flex items-center justify-center text-stone-900 hover:scale-110 transition-transform">
          <GripVertical size={16} strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
};
